import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logger } from '@/lib/logger';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Read the stored auth token safely.
 * Returns `null` when:
 *  - no token has ever been stored (first-time visitor from an ad)
 *  - the stored value is the literal string "null" / "undefined" / ""
 *  - localStorage is unavailable (privacy mode, SSR, etc.)
 * This prevents any downstream code (axios headers, JWT decoders) from
 * receiving a bogus token and crashing the initial paint.
 */
const readStoredToken = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem('token');
    if (!raw) return null;
    if (raw === 'null' || raw === 'undefined' || raw.trim() === '') return null;
    return raw;
  } catch (_) {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readStoredToken());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    try { localStorage.removeItem('token'); } catch (_) { /* noop */ }
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    // Extra defense: never call the backend with a null/empty token
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      logger.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      // New visitor / logged-out: show the landing page immediately.
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    if (newToken) {
      try { localStorage.setItem('token', newToken); } catch (_) { /* noop */ }
    }
    setToken(newToken || null);
    setUser(userData || null);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    if (newToken) {
      try { localStorage.setItem('token', newToken); } catch (_) { /* noop */ }
    }
    setToken(newToken || null);
    setUser(newUser || null);
    return newUser;
  };

  const getAuthHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};